import { useCallback, useMemo } from 'react'
import {
  useApolloClient,
  useQuery,
  ApolloClient,
  NormalizedCacheObject,
  OperationVariables,
  gql,
  ApolloError,
} from '@apollo/client'
import { emitGraphQLError } from './errors'
import { refetchGraphQLFetch } from './service'
import { useIdleAndOnlineStatus } from './use-idle-and-online-status'
import { useDeepCompareMemo, ensureUIDInQuery } from './utils'
import type {
  UseGraphQLFetchProps,
  GraphQLFetchResult,
  GraphQLFetchOptions,
} from './types'

/**
 * React hook for executing generic GraphQL queries with built-in state management.
 * Uses Apollo's useQuery hook internally for optimal performance and caching.
 * Use this for custom queries with DocumentNode and variables.
 *
 * @param props - Hook configuration including query DocumentNode and variables
 * @returns Query result with data, loading state, error, and refetch function
 */
export const useGraphQLFetch = <T = unknown>({
  query,
  variables = {},
  skip = false,
  fetchPolicy, // will default to cache-first from client setup if not provided
}: UseGraphQLFetchProps): GraphQLFetchResult<T> => {
  // Get Apollo client at the top level
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>

  // Ensure UID is always included for cache normalization
  const normalizedQuery = useMemo(() => {
    if (!query) {
      return gql`
        query EmptyQuery {
          __typename
        }
      `
    }
    return ensureUIDInQuery(query)
  }, [query])

  // Use Apollo's useQuery hook for optimal state management
  const {
    data,
    loading,
    error,
    refetch: apolloRefetch,
    fetchMore,
  } = useQuery<T>(normalizedQuery, {
    variables,
    skip: skip || !query || !query,
    fetchPolicy,
    errorPolicy: 'all', // Return both data and errors
    notifyOnNetworkStatusChange: true,
  })
  // Shared idle/online status to gate refetch behavior
  const { isIdle, isOffline } = useIdleAndOnlineStatus(true)

  // Stabilize variables with deep comparison to avoid unnecessary re-renders
  const stableVariables = useDeepCompareMemo(variables)

  // Memoize query options for the custom refetch function
  const queryOptions = useMemo((): GraphQLFetchOptions | null => {
    if (skip || !query) return null

    return {
      query,
      variables: stableVariables,
      fetchPolicy,
    }
  }, [skip, query, fetchPolicy, stableVariables])

  /**
   * Enhanced refetch function that works with our service layer.
   * Provides additional flexibility for variable overrides.
   */
  const refetch = useCallback(
    async (overrideVariables?: OperationVariables) => {
      // Block refetch attempts while idle or offline
      if (isIdle || isOffline) return
      if (!queryOptions) return

      // If no override variables, use Apollo's built-in refetch
      if (!overrideVariables || Object.keys(overrideVariables).length === 0) {
        return apolloRefetch()
      }

      // For variable overrides, use our service layer
      try {
        const result = await refetchGraphQLFetch<T>(
          apolloClient,
          queryOptions,
          overrideVariables,
        )
        return result
      } catch (err) {
        throw err as ApolloError
      }
    },
    [queryOptions, apolloRefetch, apolloClient, isIdle, isOffline],
  )

  return {
    data: data || null,
    loading,
    error: (() => {
      if (error) emitGraphQLError(error, { operation: 'query' })
      return error || null
    })(),
    refetch,
    fetchMore,
  }
}
